import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';

const ContactSection = ({ typography = {} }) => {
  // Default typography classes in case they're not provided
  const defaultTypography = {
    heading3: 'text-3xl md:text-4xl font-bold',
    heading4: 'text-xl md:text-2xl font-semibold',
    subtitle: 'text-lg md:text-xl',
    body: 'text-base',
    caption: 'text-sm font-medium',
    button: 'text-base font-medium'
  };

  // Merge provided typography with defaults
  const finalTypography = { ...defaultTypography, ...typography };

  return (
    <section
      id='contact'
      className='py-8 md:py-10 lg:py-12 bg-gray-50 relative'
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          className='text-center mb-6 md:mb-8'
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className={`${finalTypography.heading3} mb-4 text-blue-600`}>
            Let's Build Something Amazing
          </h2>
          <p className={`${finalTypography.subtitle} text-gray-600 max-w-2xl mx-auto`}>
            Ready to transform your ideas into reality? Get in touch today.
          </p>
        </motion.div>

        <div className='grid lg:grid-cols-2 gap-8'>
          {/* Contact Information */}
          <motion.div
            className='space-y-6'
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Email */}
            <div className='flex items-start gap-4'>
              <div className='flex-shrink-0 bg-blue-100 p-3 rounded-xl'>
                <Mail className='w-5 h-5 text-blue-600' />
              </div>
              <div className='flex-1'>
                <div className={`${finalTypography.caption} text-gray-900 mb-1`}>
                  Email
                </div>
                <div className={`${finalTypography.body} text-gray-600`}>
                  aeggrouphelpdesk@gmail.com
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className='flex items-start gap-4'>
              <div className='flex-shrink-0 bg-teal-100 p-3 rounded-xl'>
                <Phone className='w-5 h-5 text-teal-600' />
              </div>
              <div className='flex-1'>
                <div className={`${finalTypography.caption} text-gray-900 mb-1`}>
                  Phone
                </div>
                <div className={`${finalTypography.body} text-gray-600`}>
                  +91 6268558700
                </div>
              </div>
            </div>

            {/* Address */}
            <div className='flex items-start gap-4'>
              <div className='flex-shrink-0 bg-purple-100 p-3 rounded-xl'>
                <MapPin className='w-5 h-5 text-purple-600' />
              </div>
              <div className='flex-1'>
                <div className={`${finalTypography.caption} text-gray-900 mb-1`}>
                  Address
                </div>
                <div className={`${finalTypography.body} text-gray-600`}>
                  329 Kalpana Nagar, BHEL, Bhopal, Madhya Pradesh, Pin No.: 462022
                </div>
              </div>
            </div>

            {/* Map Section */}
            <div className='mt-8'>
              <h3 className={`${finalTypography.heading4} text-gray-900 mb-4`}>
                Our Location
              </h3>
              <div className='relative rounded-xl overflow-hidden shadow-lg border border-gray-200'>
                <iframe
                  title='AEG Consultancy Services Location Map'
                  className='w-full h-48 md:h-64'
                  src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7331.4140045990725!2d77.47558749999999!3d23.253746800000005!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c42033f80d115%3A0x3248bd8eb9222d16!2sAssurance%20Educational%20Group%20%3A%20MTECH%20THESIS%2C%20PHD%20GUIDANCE%2C%20PAPER%20PUBLICATION!5e0!3m2!1sen!2sin!4v1775561903792!5m2!1sen!2sin'
                  style={{ border: 0 }}
                  allowFullScreen=''
                  loading='lazy'
                  referrerPolicy='no-referrer-when-downgrade'
                ></iframe>
              </div>
              <motion.button
                className='mt-4 w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-all duration-300'
                whileHover={{ scale: 1.02, boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() =>
                  window.open(
                    'https://maps.app.goo.gl/be1aiUXio6vTMezt5',
                    '_blank'
                  )
                }
              >
                <MapPin className='w-4 h-4' />
                Open in Google Maps
              </motion.button>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className='bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-100'
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className='space-y-5'>
              <div className='grid md:grid-cols-2 gap-4'>
                <input
                  type='text'
                  placeholder='Your Name'
                  className={`${finalTypography.body} w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 transition-all duration-200`}
                />
                <input
                  type='email'
                  placeholder='Your Email'
                  className={`${finalTypography.body} w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 transition-all duration-200`}
                />
              </div>
              <input
                type='text'
                placeholder='Subject'
                className={`${finalTypography.body} w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 transition-all duration-200`}
              />
              <input
                type='Mobile Number'
                placeholder='Your Mobile Number'
                className={`${finalTypography.body} w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 transition-all duration-200`}
              />
              <textarea
                placeholder='Your Message'
                rows='5'
                className={`${finalTypography.body} w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500 resize-none transition-all duration-200`}
              ></textarea>
              <motion.button
                className={`w-full bg-blue-600 text-white py-4 rounded-xl ${finalTypography.button} hover:bg-blue-700 transition-all duration-300`}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
                }}
                whileTap={{ scale: 0.98 }}
              >
                Send Message
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;